// This function takes a (potentially filtered) customerFoodListPerLocation and
// builds a dictionary of html strings - one entry per
// "<distributionLocation> - <pickupLocation>" - that matches the UI seen in
// WholeOrderAccordion when the user has checked "Display All Customers".  Each
// pickup location shows its newly ordered and pantry items, and each item lists
// the customers that ordered it along with how many they took.  Returning a
// dict (rather than one combined string) lets the caller produce a separate
// word doc per location, mirroring how CreateDownloadOrdersString works for
// the public hub list format.

// Converts an integer to a roman numeral.  Used to number the customers under
// each item (same convention as CustomersOrderedItem).
function toRoman(num) {
  const roman = [
    ["M", 1000],
    ["CM", 900],
    ["D", 500],
    ["CD", 400],
    ["C", 100],
    ["XC", 90],
    ["L", 50],
    ["XL", 40],
    ["X", 10],
    ["IX", 9],
    ["V", 5],
    ["IV", 4],
    ["I", 1],
  ];
  let result = "";
  let remaining = num;
  for (const [letter, value] of roman) {
    while (remaining >= value) {
      result += letter;
      remaining -= value;
    }
  }
  return result;
}

export default function CreateDownloadWholeCommunityOrdersString(
  sortedPickupLocations,
  customerFoodListPerLocation,
) {
  // One html string per "<distributionLocation> - <pickupLocation>" - matches
  // the location keys the selection dialog uses so the caller can iterate and
  // download one doc per location.
  const downloadStrings = {};

  // Cycle through each distribution location and their pickup locations.
  Object.keys(sortedPickupLocations).forEach((distributionLocation) => {
    Object.keys(customerFoodListPerLocation[distributionLocation]).forEach(
      (pickupLocation) => {
        // Header showing just the pickup location (Onsite / community hub name).
        let locationString = `<div><h3 style="font-size: 18px; margin: 15px 0px 8px 0px;">
          <b>${pickupLocation}</b>
        </h3>`;

        // Combine newly ordered items and pantry items into one list - the
        // printed document no longer separates them.
        const allItems = [
          ...(customerFoodListPerLocation[distributionLocation][pickupLocation]
            .orderedItems || []),
          ...(customerFoodListPerLocation[distributionLocation][pickupLocation]
            .pantryItems || []),
        ];

        if (allItems.length > 0) {
          // Group the items by farm so each farm name is only shown once as
          // a header above its items.  A Map keeps first-seen ordering.
          const itemsByFarm = new Map();
          allItems.forEach((food) => {
            if (!itemsByFarm.has(food.farmName)) {
              itemsByFarm.set(food.farmName, []);
            }
            itemsByFarm.get(food.farmName).push(food);
          });

          itemsByFarm.forEach((farmItems, farmName) => {
            // Farm heading - only shown once per farm.
            locationString += `<p style="margin: 10px 0px 4px 10px; font-size: 16px;">
              <b><i>${farmName}</i></b>
            </p>`;

            farmItems.forEach((food, itemNumber) => {
              // Compute the individual price/description the same way the
              // accordion does so the printed amount matches the UI.
              let individualPrice = parseFloat(
                food.price /
                  (food.distributionQuantity / food.individualQuantity),
              );
              let individualDescription = food.individualDescription;

              if (food.suggestedPrice) {
                individualDescription = individualDescription.concat(
                  " - Suggested Donation of $",
                  individualPrice,
                );
                individualPrice = 0.0;
              }

              if (food.packageFee > 0) {
                individualPrice += parseFloat(food.packageFee);
              }
              individualPrice = individualPrice.toFixed(2);

              // Item line - farm name omitted (it's in the heading above) and
              // the description is underlined + yellow-highlighted so it
              // stands out for the packer.  Numbering restarts per farm.
              locationString += `<div style="border: solid 1px; margin-bottom: 4px; page-break-inside: avoid;">
                <p style="margin: 0px 0px 0px 20px; font-size: 15px;">
                  ${itemNumber + 1}. <b>${
                    food.item
                  }</b> <u style="background-color: yellow;">${individualDescription}</u>
                  <u style="color: red;">x${food.quantity}</u>
                </p>`;

              // Underneath list out every customer that ordered this item.
              // The description is omitted here since it's already on the line
              // above - we just show the customer name and how many they got.
              // Sorted by first name, case-insensitive.
              if (food.customers) {
                const sortedCustomerNames = Object.keys(food.customers).sort(
                  (a, b) => {
                    const firstA = (a.split("-")[0] || "").toLowerCase();
                    const firstB = (b.split("-")[0] || "").toLowerCase();
                    return firstA.localeCompare(firstB);
                  },
                );
                sortedCustomerNames.forEach((customerName, custIndex) => {
                  // The stored key is "First-Last-UserId" so keep only the
                  // first two parts for the readable name.
                  const nameParts = customerName.split("-");
                  const readableName = nameParts.slice(0, 2).join(" ");
                  // Only colour the multiplier red when the customer took
                  // more than one - keeps single-quantity lines understated.
                  const customerQuantity = food.customers[customerName];
                  const customerMultiplierColour =
                    customerQuantity > 1 ? "red" : "black";
                  locationString += `<p style="margin: 2px 0px 2px 60px; font-size: 14px;">
                    ${toRoman(custIndex + 1)}. ${readableName}
                    <u style="color: ${customerMultiplierColour};">x${customerQuantity}</u>
                  </p>`;
                });
              }

              locationString += `</div>`;
            });
          });
        }

        locationString += `</div>`;
        downloadStrings[`${distributionLocation} - ${pickupLocation}`] =
          locationString;
      },
    );
  });

  return downloadStrings;
}
